import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

/** @extends {Component} */
class CartFrequencySelectorComponent extends Component {
  #busy = false;

  async handleSellingPlanChange(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const newPlanId = select.value;
    const currentPlanId = this.dataset.currentPlan;

    if (newPlanId === currentPlanId || this.#busy) return;

    this.#busy = true;
    this.#setLoading(true);

    const line = Number(this.dataset.line);
    const variantId = Number(this.dataset.variantId);
    const quantity = Number(this.dataset.quantity);

    const cartItemsEl = this.closest('cart-items-component');
    const sectionId = cartItemsEl?.dataset?.sectionId;

    if (!sectionId) {
      this.#busy = false;
      this.#setLoading(false);
      return;
    }

    try {
      await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
        body: JSON.stringify({ line, quantity: 0 }),
      }));

      const addResponse = await fetch(Theme.routes.cart_add_url, fetchConfig('json', {
        body: JSON.stringify({
          id: variantId,
          quantity,
          selling_plan: Number(newPlanId),
          sections: sectionId,
        }),
      }));

      const addData = await addResponse.json();

      if (addData.status) {
        console.error('CartFrequencySelector: Failed to re-add item', addData.description);
        await this.#rollback(line, variantId, quantity, Number(currentPlanId), sectionId);
        return;
      }

      const sectionHtml = addData.sections?.[sectionId];
      const newCartItemCount = sectionHtml
        ? this.#extractItemCount(sectionHtml)
        : null;

      if (sectionHtml) {
        morphSection(sectionId, sectionHtml);
      }

      document.dispatchEvent(
        new CartUpdateEvent(addData, sectionId, {
          source: 'cart-frequency-selector',
          ...(newCartItemCount != null && { itemCount: newCartItemCount }),
        })
      );
    } catch (error) {
      console.error('CartFrequencySelector: Selling plan change failed', error);
      select.value = currentPlanId || '';
    } finally {
      this.#busy = false;
      this.#setLoading(false);
    }
  }

  async #rollback(_line, variantId, quantity, originalPlanId, sectionId) {
    try {
      const resp = await fetch(Theme.routes.cart_add_url, fetchConfig('json', {
        body: JSON.stringify({
          id: variantId,
          quantity,
          selling_plan: originalPlanId,
          sections: sectionId,
        }),
      }));
      const data = await resp.json();

      if (data.sections?.[sectionId]) {
        morphSection(sectionId, data.sections[sectionId]);
      }
    } catch (rollbackError) {
      console.error('CartFrequencySelector: Rollback also failed', rollbackError);
    }
  }

  #extractItemCount(html) {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const countEl = doc.querySelector('[ref="cartItemCount"]');
    return countEl ? parseInt(countEl.textContent || '0', 10) : null;
  }

  #setLoading(loading) {
    this.classList.toggle('cart-frequency-selector--loading', loading);
    const select = this.querySelector('select');
    if (select) select.disabled = loading;
  }
}

if (!customElements.get('cart-frequency-selector-component')) {
  customElements.define('cart-frequency-selector-component', CartFrequencySelectorComponent);
}
